import React, { useState, useEffect } from "react";
import "./Login.css";
import googleLogo from "../images/logo-google-svgrepo-com.svg";
import logo from "../images/download.svg";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "../supabase";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import image1 from "../images/dark-green-pickleball-court.jpg";
import image2 from "../images/padel-court.jpg";

const slideshowImages = [image1, image2];

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [titlePrefix, setTitlePrefix] = useState("Pickle");
  const [scrollDirection, setScrollDirection] = useState("down");

  useEffect(() => {
    const checkSession = async () => {
      const {
        data: { session },
        error,
      } = await supabase.auth.getSession();
      if (error) {
        console.error("Login.js: Error checking session:", error);
        setError("Failed to check session. Please try again.");
        return;
      }
      if (session) {
        // //console.log('Login.js: User already logged in:', session.user);
        const redirectTo = location.state?.from || "/";
        navigate(redirectTo, { state: location.state });
      }
    };

    checkSession();

    const queryParams = new URLSearchParams(location.search);
    if (location.state?.logoutSuccess) {
      setSuccessMessage("You have been successfully logged out.");
    } else if (queryParams.get("registered") === "true") {
      setSuccessMessage(
        "Registration successful! Please verify your email and log in with your new account."
      );
    }

    const { data: authListener } = supabase.auth.onAuthStateChange(
      (event, session) => {
        // //console.log('Login.js: Auth state changed:', event, session);
        if (event === "SIGNED_IN" && session) {
          // //console.log('Login.js: User signed in:', session.user);
          const redirectTo = location.state?.from || "/";
          navigate(redirectTo, { state: location.state });
        }
      }
    );

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, [navigate, location.state, location.search]);

  useEffect(() => {
    let isFirstRun = true;

    const interval = setInterval(() => {
      if (isFirstRun) {
        isFirstRun = false;
        return;
      }

      setCurrentSlide((prevSlide) => (prevSlide + 1) % slideshowImages.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [slideshowImages.length]);

  useEffect(() => {
    const newTitlePrefix = currentSlide === 0 ? "Pickle" : "Padel";
    const newDirection = currentSlide === 0 ? "down" : "up";
    setTitlePrefix(newTitlePrefix);
    setScrollDirection(newDirection);
  }, [currentSlide]);

  const handleGoogleLogin = async () => {
    try {
      setError(null);
      setSuccessMessage(null);
      // //console.log('Login.js: Initiating Google Sign-In');
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: window.location.origin,
        },
      });
      if (error) {
        console.error("Login.js: Error during Google login:", error);
        setError(`Google Login Failed: ${error.message}`);
      }
    } catch (error) {
      console.error("Login.js: Unexpected error during Google login:", error);
      setError("An unexpected error occurred. Please try again.");
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);
    setSuccessMessage(null);

    try {
      // //console.log('Login.js: Attempting to log in with email:', email);
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        console.error("Login.js: Error during email login:", error);
        setError(`Login Failed: ${error.message}`);
        return;
      }

      // //console.log('Login.js: Login successful');
    } catch (error) {
      console.error("Login.js: Unexpected error during email login:", error);
      setError("An unexpected error occurred. Please try again.");
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleLogoClick = () => {
    // //console.log("Login.js: handleLogoClick called");
    const defaultState = {
      selectedBookingType: "Bookings",
      selectedProfile: false,
      selectedSport: null,
      selectedDate: null,
      selectedCourtType: null,
      selectedCourt: null,
      selectedTimeslots: [],
    };
    // //console.log("Login.js: Navigating to / with state:", defaultState);
    navigate("/", { state: defaultState });
  };

  return (
    <div className="login-page-container login-page">
      <div className="login-left-section">
        <div className="slideshow">
          {slideshowImages.map((image, index) => (
            <div
              key={index}
              className={`slide ${index === currentSlide ? "active" : ""}`}
              style={{
                backgroundImage: `url(${
                  typeof image === "string" ? image : image.default
                })`,
              }}
            ></div>
          ))}
        </div>
      </div>

      <div className="login-right-section">
        <div
          className="logo-container"
          onClick={handleLogoClick}
          style={{ cursor: "pointer" }}
        >
          <img
            src={logo}
            alt="PickleDepot/PadelDepot Logo"
            className="login-logo"
          />
        </div>
        <div className="title-wrapper">
          <div className="title-container">
            <h2 className="login-title">
              {/* <span className="title-prefix-wrapper">
                <span className={`title-prefix ${scrollDirection}`}>
                  <span className="title-prefix-item">Pickle</span>
                  <span className="title-prefix-item">Padel</span>
                  <span className="title-prefix-item">Pickle</span>
                </span>
              </span> */}
              <span className="title-suffix">PickleDepot & PadelDepot</span>
            </h2>
          </div>
        </div>
        {successMessage && <p className="login-success">{successMessage}</p>}
        {error && <p className="login-error">{error}</p>}
        <form className="login-form" onSubmit={handleSubmit}>
          <div className="login-form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              className="login-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="Enter your email"
            />
          </div>

          <div className="login-form-group">
            <label htmlFor="password">Password</label>
            <div className="password-container">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                className="login-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="Enter your password"
              />
              <span
                className="password-toggle"
                onClick={togglePasswordVisibility}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </span>
            </div>
          </div>

          <p className="login-forgot-password">
            <a href="/forgot-password" className="login-link">
              Forgot Password?
            </a>
          </p>

          <button
            type="submit"
            className="login-button"
            aria-label="Login with email and password"
          >
            Login
          </button>

          <p className="login-divider">Or</p>

          <button
            type="button"
            className="login-button login-google-button"
            onClick={handleGoogleLogin}
            aria-label="Login with Google"
          >
            <div className="google-button-content">
              <img src={googleLogo} alt="Google Logo" />
              <span>Login with Google</span>
            </div>
          </button>
        </form>

        <p className="login-register-link">
          Don't have an account?{" "}
          <a href="/registration" className="login-link">
            Register Here
          </a>
        </p>
      </div>
    </div>
  );
};

export default Login;
